import { useMemo } from "react";
import { OccupationType, OccupationTypesDto } from "../api/dto/OccupationTypes";
import {
  OccupationTitle,
  OccupationTitlesDto,
} from "../api/dto/OccupationTitles";

export type JoinedOccupation = {
  occupationTitleId: number | string;
  occupationTitle: string;
  occupationTypeId: number | string;
  occupationType?: string;
};

export function buildOccupationSelection(
  occupationTypes?: OccupationType[],
  occupationTitles?: OccupationTitle[],
) {
  const typeById = new Map<number | string, string>();
  for (const t of occupationTypes ?? []) {
    typeById.set(t.occupationTypeId, t.occupationType);
  }

  const joined: JoinedOccupation[] = (occupationTitles ?? []).map((title) => {
    const typeId = title.occupationTypeId;
    const occupationType = typeId == null ? undefined : typeById.get(typeId);
    return {
      occupationTitleId: title.occupationTitleId,
      occupationTitle: title.occupationTitle,
      occupationTypeId: typeId as number | string,
      occupationType,
    } as JoinedOccupation;
  });

  const getTypeLabelById = (typeId?: number | string | null) => {
    if (typeId == null) return "Unknown";
    return typeById.get(typeId) ?? "Unknown";
  };

  const findByTitleId = (
    titleId?: number | string | null,
  ): JoinedOccupation | undefined => {
    if (titleId == null) return undefined;
    return joined.find((j) => j.occupationTitleId === titleId);
  };

  return { joined, getTypeLabelById, findByTitleId };
}

export function useOccupationSelection(
  occupationTypes: OccupationTypesDto["occupationTypes"] = [],
  occupationTitles: OccupationTitlesDto["occupationTitles"] = [],
) {
  // Build a map for quick type label lookup
  return useMemo(() => {
    return buildOccupationSelection(occupationTypes, occupationTitles);
  }, [occupationTypes, occupationTitles]);
}
